export const INCH = 25.4;
export const LB = 0.45359237;
export const FINISHES = ["Plain", "Zinc Plated", "Hot-Dip Galvanised", "Painted", "Black"];
export const FINISH_INFO = {
  Plain: "Bare mild steel, lightly oiled. For painting or indoor use.",
  "Zinc Plated": "Electroplated zinc for a bright, corrosion-resistant surface.",
  "Hot-Dip Galvanised": "Dipped in molten zinc for a thick coat suited to outdoor exposure.",
  Painted: "Industrial enamel or powder coat in standard colours.",
  Black: "Mill-black finish, as rolled, for concrete embedment.",
};
export const SWATCH = { Plain: "#9A9C9E", "Zinc Plated": "#C9CDD0", "Hot-Dip Galvanised": "#A7ADB1", Painted: "#2F4F3E", Black: "#2A2826" };
export const PROFILE_FIELDS = { LBH: ["L", "B", "H"], ROD: ["L", "D"], BOLT: ["L", "D", "T", "K"], STANDARD: [] };
export const FIELD_LABEL = { L: "Length (L)", B: "Breadth (B)", H: "Height / Thickness (H)", D: "Diameter (D)", T: "Thread length", K: "Bend / hook" };
export const QTY_UNITS = ["pieces", "kg", "MT", "cartons"];

export function fieldsFor(p) {
  if (!p) return [];
  const f = [...(PROFILE_FIELDS[p.dimension_profile] || [])];
  if (p.dimension_profile === "LBH" && p.shape) f.push("D");
  return f;
}
export const fieldLabel = (p, k) => (k === "D" && p?.shape ? "Hole diameter (D)" : k === "L" && p?.shape === "round_washer" ? "Outer diameter (L)" : FIELD_LABEL[k]);

export const DEFAULT_CFG = { density: 7850, threaded_factor: 0.88, carton_tare_kg: 0.5, pallet_tare_kg: 25, container_20: 21000, container_40: 26000, finish_factors: { "Hot-Dip Galvanised": 0.04, "Zinc Plated": 0.005 } };

export function dimsFromVariant(variant, unit) {
  const out = {};
  Object.entries(variant?.dims || {}).forEach(([k, v]) => { out[k] = unit === "inch" ? +(v / INCH).toFixed(3) : +(+v).toFixed(1); });
  return out;
}

export function newLine(product, extra = {}) {
  const v = product?.variants?.[0];
  const unit = extra.unit || "inch";
  return { key: Math.random().toString(36).slice(2), product_slug: product?.slug || "", product_name: product?.name || "", variant: v?.name || "", finish: product?.finishes?.[0] || "", unit, dims: dimsFromVariant(v, unit), thread_spec: "", quantity: "", qty_unit: "pieces", attachment: null, ...extra };
}

export function pieceWeight(product, line, cfg = DEFAULT_CFG) {
  const variant = product.variants.find((x) => x.name === line.variant) || product.variants[0];
  const mm = (k) => { const n = parseFloat(line.dims?.[k]); return Number.isFinite(n) && n > 0 ? n * (line.unit === "inch" ? INCH : 1) / 1000 : 0; };
  const [L, B, H, D, K] = ["L", "B", "H", "D", "K"].map(mm);
  const rho = +cfg.density || 7850;
  let w = 0;
  const prof = product.dimension_profile;
  if (prof === "ROD" && L && D) w = (Math.PI / 4) * D * D * L * rho * (product.threaded ? +cfg.threaded_factor || 0.88 : 1);
  else if (prof === "BOLT" && L && D) w = (Math.PI / 4) * D * D * (L + K) * rho;
  else if (prof === "LBH" && product.shape === "round_washer" && L && H) w = (Math.PI / 4) * (L * L - D * D) * H * rho;
  else if (prof === "LBH" && L && B && H) w = (L * B * H - (D ? (Math.PI / 4) * D * D * H : 0)) * rho;
  const source = w > 0 ? "formula" : "catalogue";
  if (!(w > 0)) w = +variant?.unit_weight_kg || 0;
  const f = +(cfg.finish_factors?.[line.finish] || 0);
  return { wpp: w * (1 + f), source, variant };
}

export function computeLine(product, line, cfg = DEFAULT_CFG) {
  if (!product) return null;
  const { wpp, source, variant } = pieceWeight(product, line, cfg);
  const ppc = +variant?.pcs_per_carton || 100;
  const cpp = +variant?.cartons_per_pallet || 40;
  const q = parseFloat(line.quantity) || 0;
  const u = line.qty_unit;
  let pcs = u === "kg" ? q / (wpp || 1) : u === "MT" ? (q * 1000) / (wpp || 1) : u === "cartons" ? q * ppc : q;
  pcs = wpp > 0 || u === "pieces" || u === "cartons" ? Math.ceil(pcs) : 0;
  const net = pcs * wpp;
  const cartons = pcs ? Math.ceil(pcs / ppc) : 0;
  const pallets = cartons ? Math.ceil(cartons / cpp) : 0;
  const tare = cartons * (+(product.carton_tare_kg ?? cfg.carton_tare_kg) || 0) + pallets * (+cfg.pallet_tare_kg || 0);
  return { pcs, wpp, net, gross: net + tare, cartons, pallets, source };
}

export function totals(results, cfg = DEFAULT_CFG) {
  const t = results.filter(Boolean).reduce((a, r) => ({ net: a.net + r.net, gross: a.gross + r.gross, cartons: a.cartons + r.cartons, pallets: a.pallets + r.pallets, pcs: a.pcs + r.pcs }), { net: 0, gross: 0, cartons: 0, pallets: 0, pcs: 0 });
  return { ...t, pct20: (t.gross / (+cfg.container_20 || 21000)) * 100, pct40: (t.gross / (+cfg.container_40 || 26000)) * 100 };
}

export const fmtW = (kg, sys) => (sys === "imperial" ? `${(kg / LB).toLocaleString(undefined, { maximumFractionDigits: kg / LB < 10 ? 3 : 1 })} lbs` : `${kg.toLocaleString(undefined, { maximumFractionDigits: kg < 10 ? 3 : 1 })} kg`);
export const fmtLen = (mm, sys) => (mm == null ? "—" : sys === "imperial" ? `${+(mm / INCH).toFixed(3)}″` : `${+(+mm).toFixed(1)} mm`);

export function summarize(t) {
  if (!t.gross) return "";
  const p20 = t.pct20, p40 = t.pct40;
  if (p20 <= 100) return `This is roughly ${p20.toFixed(0)}% of a 20 ft container by weight (${p40.toFixed(0)}% of a 40 ft).`;
  if (p40 <= 100) return `This exceeds a 20 ft container; it is roughly ${p40.toFixed(0)}% of a 40 ft container by weight.`;
  return `This needs about ${Math.ceil(p40 / 100)} × 40 ft containers by weight (${p40.toFixed(0)}% of one 40 ft).`;
}
